const API_BASE = '/api';

// Token management - will be set by AuthContext
let authToken = null;

export function setAuthToken(token) {
  authToken = token;
}

export function getAuthToken() {
  return authToken;
}

function getHeaders(extraHeaders = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  return headers;
}

async function request(url, options = {}) {
  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: getHeaders(options.headers),
  });
  if (res.status === 401) {
    // Login endpoints should show the error normally, not reload
    const isLoginRequest = url.startsWith('/auth/login') || url.startsWith('/auth/guest');
    if (!isLoginRequest && authToken) {
      // Token expired or invalid - clear session and reload
      sessionStorage.removeItem('mcmon_token');
      sessionStorage.removeItem('mcmon_user');
      window.location.reload();
      throw new Error('Sitzung abgelaufen');
    }
    // For login attempts, fall through to normal error handling below
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

// SSE helper for streaming endpoints (modpack import, server creation with modpack)
function streamRequest(url, body, onEvent) {
  return new Promise((resolve, reject) => {
    fetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
    }).then(res => {
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      function read() {
        reader.read().then(({ done, value }) => {
          if (done) {
            resolve();
            return;
          }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                onEvent(data);
              } catch { /* ignore parse errors */ }
            }
          }
          read();
        }).catch(reject);
      }
      read();
    }).catch(reject);
  });
}

export const api = {
  // Auth
  loginGuest: () => request('/auth/guest', { method: 'POST', body: JSON.stringify({}) }),
  login: (username, password) => request('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  getMe: () => request('/auth/me'),
  changePassword: (currentPassword, newPassword) => request('/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),

  // Admin - User Management
  getUsers: () => request('/admin/users'),
  createUser: (data) => request('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),
  resetPassword: (id, newPassword) => request(`/admin/users/${id}/reset-password`, { method: 'PUT', body: JSON.stringify({ newPassword }) }),
  getGuestLog: () => request('/admin/guests'),
  clearGuestLog: () => request('/admin/guests', { method: 'DELETE' }),

  // Info / Rules
  getRules: () => request('/info/rules'),
  updateRules: (data) => request('/info/rules', { method: 'PUT', body: JSON.stringify(data) }),

  // Servers
  getServers: () => request('/servers'),
  getServer: (id) => request(`/servers/${id}`),
  getNextPort: () => request('/servers/next-port'),
  getVersions: () => request('/servers/versions'),
  createServer: (data) => request('/servers', { method: 'POST', body: JSON.stringify(data) }),
  createServerWithModpack: (data, onEvent) => streamRequest('/servers', data, onEvent),
  updateServer: (id, data) => request(`/servers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteServer: (id) => request(`/servers/${id}`, { method: 'DELETE' }),
  startServer: (id) => request(`/servers/${id}/start`, { method: 'POST' }),
  stopServer: (id) => request(`/servers/${id}/stop`, { method: 'POST' }),
  killServer: (id) => request(`/servers/${id}/kill`, { method: 'POST' }),
  sendCommand: (id, command) => request(`/servers/${id}/command`, { method: 'POST', body: JSON.stringify({ command }) }),
  getProperties: (id) => request(`/servers/${id}/properties`),
  updateProperties: (id, props) => request(`/servers/${id}/properties`, { method: 'PUT', body: JSON.stringify(props) }),
  getChatHistory: (id, limit = 200) => request(`/servers/${id}/chat?limit=${limit}`),

  // World Download/Upload
  getWorldDownloadUrl: (serverId) => `${API_BASE}/servers/${serverId}/world/download`,
  uploadWorld: async (serverId, file) => {
    const formData = new FormData();
    formData.append('worldZip', file);
    const res = await fetch(`${API_BASE}/servers/${serverId}/world/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Upload fehlgeschlagen');
    }
    return res.json();
  },

  // Mods
  searchMods: (query, loader, version) => {
    const params = new URLSearchParams({ query });
    if (loader) params.set('loader', loader);
    if (version) params.set('version', version);
    return request(`/mods/search?${params}`);
  },
  getModVersions: (projectId, loader, gameVersion) => {
    const params = new URLSearchParams();
    if (loader) params.set('loader', loader);
    if (gameVersion) params.set('gameVersion', gameVersion);
    return request(`/mods/versions/${projectId}?${params}`);
  },
  downloadMod: (url, fileName) => request('/mods/download', { method: 'POST', body: JSON.stringify({ url, fileName }) }),
  getModLibrary: () => request('/mods/library'),
  deleteModFromLibrary: (fileName) => request(`/mods/library/${encodeURIComponent(fileName)}`, { method: 'DELETE' }),
  getUploads: () => request('/mods/uploads'),
  importFromUpload: (fileName) => request('/mods/import', { method: 'POST', body: JSON.stringify({ fileName }) }),
  importToServer: (fileName, serverId) => request('/mods/import-to-server', { method: 'POST', body: JSON.stringify({ fileName, serverId }) }),
  getServerMods: (serverId) => request(`/mods/server/${serverId}`),
  installModToServer: (serverId, fileName) => request(`/mods/server/${serverId}/install`, { method: 'POST', body: JSON.stringify({ fileName }) }),
  toggleMod: (serverId, fileName) => request(`/mods/server/${serverId}/toggle`, { method: 'POST', body: JSON.stringify({ fileName }) }),
  removeModFromServer: (serverId, fileName) => request(`/mods/server/${serverId}/${encodeURIComponent(fileName)}`, { method: 'DELETE' }),

  // Players
  getOnlinePlayers: (serverId) => request(`/players/${serverId}/online`),
  getWhitelist: (serverId) => request(`/players/${serverId}/whitelist`),
  addToWhitelist: (serverId, name) => request(`/players/${serverId}/whitelist`, { method: 'POST', body: JSON.stringify({ name }) }),
  removeFromWhitelist: (serverId, name) => request(`/players/${serverId}/whitelist/${name}`, { method: 'DELETE' }),
  getBanned: (serverId) => request(`/players/${serverId}/banned`),
  banPlayer: (serverId, name, reason) => request(`/players/${serverId}/ban`, { method: 'POST', body: JSON.stringify({ name, reason }) }),
  unbanPlayer: (serverId, name) => request(`/players/${serverId}/ban/${name}`, { method: 'DELETE' }),
  opPlayer: (serverId, name) => request(`/players/${serverId}/op`, { method: 'POST', body: JSON.stringify({ name }) }),
  deopPlayer: (serverId, name) => request(`/players/${serverId}/deop`, { method: 'POST', body: JSON.stringify({ name }) }),
  kickPlayer: (serverId, name, reason) => request(`/players/${serverId}/kick`, { method: 'POST', body: JSON.stringify({ name, reason }) }),

  // Whitelist Requests
  getPendingWhitelistRequests: () => request('/players/whitelist-requests/pending'),
  getWhitelistRequests: (serverId, status) => request(`/players/${serverId}/whitelist-requests${status ? `?status=${status}` : ''}`),
  approveWhitelistRequest: (serverId, requestId) => request(`/players/${serverId}/whitelist-requests/${requestId}/approve`, { method: 'POST' }),
  denyWhitelistRequest: (serverId, requestId, notes) => request(`/players/${serverId}/whitelist-requests/${requestId}/deny`, { method: 'POST', body: JSON.stringify({ notes }) }),

  // Whitelist Toggle
  getWhitelistStatus: (serverId) => request(`/players/${serverId}/whitelist-status`),
  toggleWhitelist: (serverId, enabled) => request(`/players/${serverId}/whitelist-toggle`, { method: 'POST', body: JSON.stringify({ enabled }) }),

  // OPs
  getOps: (serverId) => request(`/players/${serverId}/ops`),

  // Action History
  getActionHistory: (filters) => {
    const params = new URLSearchParams();
    if (filters?.serverId) params.set('serverId', filters.serverId);
    if (filters?.action) params.set('action', filters.action);
    if (filters?.targetPlayer) params.set('targetPlayer', filters.targetPlayer);
    if (filters?.limit) params.set('limit', filters.limit);
    return request(`/players/history?${params}`);
  },

  // Player Whitelist Status (for logged-in players)
  getMyWhitelistStatus: () => request('/players/my-status'),

  // System Metrics
  getSystemMetrics: () => request('/system/metrics'),

  // Backups
  getBackups: (serverId) => request(`/backups/${serverId}`),
  createBackup: (serverId) => request(`/backups/${serverId}`, { method: 'POST' }),
  deleteBackup: (serverId, fileName) => request(`/backups/${serverId}/${encodeURIComponent(fileName)}`, { method: 'DELETE' }),
  restoreBackup: (serverId, fileName) => request(`/backups/${serverId}/restore/${encodeURIComponent(fileName)}`, { method: 'POST' }),
  getBackupDownloadUrl: (serverId, fileName) => `${API_BASE}/backups/${serverId}/download/${encodeURIComponent(fileName)}`,
  getBackupSchedule: (serverId) => request(`/backups/${serverId}/schedule`),
  updateBackupSchedule: (serverId, config) => request(`/backups/${serverId}/schedule`, { method: 'PUT', body: JSON.stringify(config) }),

  // Modpacks
  getModpacks: () => request('/modpacks'),
  getModpack: (id) => request(`/modpacks/${id}`),
  getModpackUploads: () => request('/modpacks/uploads'),
  importModpack: (fileName, onEvent) => streamRequest('/modpacks/import', { fileName }, onEvent),
  createModpack: (data) => request('/modpacks', { method: 'POST', body: JSON.stringify(data) }),
  updateModpack: (id, data) => request(`/modpacks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteModpack: (id) => request(`/modpacks/${id}`, { method: 'DELETE' }),
  getModpackExportUrl: (id) => `${API_BASE}/modpacks/${id}/export`,

  // Feedback
  sendFeedback: (data) => request('/feedback', { method: 'POST', body: JSON.stringify(data) }),
  getFeedbackList: () => request('/feedback'),
  deleteFeedback: (folderId) => request(`/feedback/${encodeURIComponent(folderId)}`, { method: 'DELETE' }),
  getFeedbackScreenshotUrl: (folderId) => `${API_BASE}/feedback/${encodeURIComponent(folderId)}/screenshot?token=${authToken}`,

  // Skin Editor
  getSkins: () => request('/skins'),
  getSkin: (id) => request(`/skins/${id}`),
  getSkinImageUrl: (id) => `${API_BASE}/skins/${id}/image`,
  createSkin: (data) => request('/skins', { method: 'POST', body: JSON.stringify(data) }),
  updateSkin: (id, data) => request(`/skins/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSkin: (id) => request(`/skins/${id}`, { method: 'DELETE' }),
  setSkinShared: (id, shared) => request(`/skins/${id}/share`, { method: 'PUT', body: JSON.stringify({ shared }) }),
  cloneSkin: (id) => request(`/skins/${id}/clone`, { method: 'POST' }),
  getSharedSkins: () => request('/skins/shared'),
  getDefaultSkins: () => request('/skins/defaults'),
  getDefaultSkinImageUrl: (name) => `${API_BASE}/skins/defaults/${name}/image`,

  // Texture Pack Editor
  getTexturePacks: () => request('/texture-packs'),
  getTexturePack: (id) => request(`/texture-packs/${id}`),
  createTexturePack: (data) => request('/texture-packs', { method: 'POST', body: JSON.stringify(data) }),
  updateTexturePack: (id, data) => request(`/texture-packs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTexturePack: (id) => request(`/texture-packs/${id}`, { method: 'DELETE' }),
  setTexturePackShared: (id, shared) => request(`/texture-packs/${id}/share`, { method: 'PUT', body: JSON.stringify({ shared }) }),
  cloneTexturePack: (id) => request(`/texture-packs/${id}/clone`, { method: 'POST' }),
  getTexturePackVersions: () => request('/texture-packs/versions'),
  getTexturePackFiles: (id) => request(`/texture-packs/${id}/files`),
  getTextureFileImageUrl: (packId, fileId) => `${API_BASE}/texture-packs/${packId}/files/${fileId}/image`,
  saveTextureFile: (packId, assetPath, textureDataBase64) => request(`/texture-packs/${packId}/files`, { method: 'POST', body: JSON.stringify({ assetPath, textureDataBase64 }) }),
  deleteTextureFile: (packId, fileId) => request(`/texture-packs/${packId}/files/${fileId}`, { method: 'DELETE' }),
  importTexturePack: (packId, zipDataBase64) => request(`/texture-packs/${packId}/import`, { method: 'POST', body: JSON.stringify({ zipDataBase64 }) }),
  importTexturePackSelected: (packId, textures) => request(`/texture-packs/${packId}/import-selected`, { method: 'POST', body: JSON.stringify({ textures }) }),
  downloadTexturePackUrl: (id) => `${API_BASE}/texture-packs/${id}/download`,
  applyTexturePackToServer: (packId, serverId, opts) => request(`/texture-packs/${packId}/apply/${serverId}`, { method: 'POST', body: JSON.stringify(opts) }),
  fetchVanillaAssets: (version) => request(`/texture-packs/defaults/${version}/fetch`, { method: 'POST' }),
  getVanillaAssets: (version) => request(`/texture-packs/defaults/${version}`),
  getVanillaTextureUrl: (version, relativePath) => `${API_BASE}/texture-packs/defaults/${version}/${relativePath}`,
  getVanillaTranslations: (version) => request(`/texture-packs/defaults/${version}/translations`),

  // World Generator (OSM)
  worldgenHealth: () => request('/worldgen/health'),
  worldgenGenerate: (params) => request('/worldgen/generate', { method: 'POST', body: JSON.stringify(params) }),
  worldgenJobs: () => request('/worldgen/jobs'),
  worldgenJob: (jobId) => request(`/worldgen/jobs/${jobId}`),
  worldgenDeleteJob: (jobId) => request(`/worldgen/jobs/${jobId}`, { method: 'DELETE' }),
  worldgenProgressUrl: (jobId) => {
    const token = authToken ? `?token=${encodeURIComponent(authToken)}` : '';
    return `${API_BASE}/worldgen/progress/${jobId}${token}`;
  },
  worldgenDownloadUrl: (jobId) => `${API_BASE}/worldgen/download/${jobId}`,
  worldgenApply: (jobId, serverId, onEvent) => streamRequest(`/worldgen/apply/${jobId}/${serverId}`, {}, onEvent),
};
